import { Injectable, inject, signal } from '@angular/core';
import { AuthService } from '@core/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { Observable, firstValueFrom, from, of, timer } from 'rxjs';
import { catchError, switchMap, take, tap } from 'rxjs/operators';
import { Data } from '@angular/router';
import * as confetti from 'canvas-confetti';
import { NarratorTipsComponent } from 'src/app/components/narrator-tips/narrator-tips.component';
import { NarratorTipService } from 'src/app/services/AppRootService/narrator-tip.service';
import { GameOnboardingPopupService } from './GameOnboardingPopupService';
import { normalizeGameOnboardingPopupId } from '../components/onboarding-popup/onboarding-popup.contract';
import { AnimationWidgetService } from './AnimationWidgetService';

export type OnboardingCue = {
  index?: number;
  type?: string;
  popup_id?: string;
  location_id?: string;
  animation_id?: string;
  template_id?: string;
  duration?: number;
  [key: string]: unknown;
};

export type ActiveOnboardingResult = {
  status?: 'success' | 'fail' | 'idle' | string;
  step_id?: string | null;
  step?: {
    cues?: OnboardingCue[];
    completion_event?: string;
    step_id?: string;
  } | null;
  step_state?: {
    current_cue?: number;
    cues_fired?: number[];
    completed?: boolean;
  } | null;
  current_cue?: OnboardingCue | null;
  client_cues?: OnboardingCue[];
  pending_popups?: string[];
  awards?: Data;
  cohort?: string;
  challenge?: {
    completed?: boolean;
    completed_at?: unknown;
    rewarded?: boolean;
    issued?: boolean;
    [key: string]: unknown;
  };
};

@Injectable({ providedIn: 'root' })
export class GamesOnboardingService {
  private readonly authService = inject(AuthService);
  private readonly popupService = inject(GameOnboardingPopupService);
  private readonly matDialog = inject(MatDialog);
  private readonly narratorTipService = inject(NarratorTipService);
  private animationWidget?: AnimationWidgetService;

  /** Drives party-popper Lottie overlay on game-new during celebration cues. */
  readonly celebrationActive = signal(false);
  /** Active onboarding step id — used to keep challenge HUD visible. */
  readonly activeStepId = signal<string | null>(null);
  /** Last evaluate/action/report payload — for Onboarding QA panel. */
  readonly lastResult = signal<ActiveOnboardingResult | null>(null);
  readonly qaLog = signal<string>('');

  private celebrationClearTimer?: ReturnType<typeof setTimeout>;
  private runInFlight = false;
  /** Latest run() event waiting while runInFlight (completion events win). */
  private queuedRunEvent: string | null = null;
  private applyInFlight = false;
  /** Latest evaluate result waiting while applyInFlight (do not drop pending_popups). */
  private queuedApply: {
    res: ActiveOnboardingResult;
    triggerEvent: string | null;
  } | null = null;
  private lastReportedPopupKey: string | null = null;
  private pendingNarratorCueIndex: number | null = null;
  private pendingMicroAnimCueIndex: number | null = null;
  private pendingMicroAnimLocationId: string | null = null;
  /** When CF uses buy_button but shop is closed, HUD shows shop_entry first. */
  private pendingMicroAnimDisplayLocationId: string | null = null;
  private lastPresentedCueKey: string | null = null;
  /** Prevents narrator tip dialog spam across reloads for the same step cue. */
  private readonly presentedNarratorKeys = new Set<string>();
  /** One-shot challenge-complete celebration + journey popup per browser session. */
  private challengeCompleteFxDone = false;
  /** One-shot prize-box-opened celebration + journey popup per browser session. */
  private prizeBoxOpenedFxDone = false;
  /** Last evaluate/report event — gates event-driven celebration cues. */
  private lastTriggerEvent: string | null = null;
  /** Pending popups the user already dismissed (scoped per uid — CF may still echo them). */
  private readonly dismissedPendingPopups = new Set<string>();
  private dismissTrackingUid: string | null = null;
  /**
   * One-shot per session: HUD incomplete→complete and claim dialog share this so we
   * do not double-fire first_challenge_completed (would break celebration / popup).
   */
  private firstChallengeCompletedRequested = false;
  /** null until first HUD sample — seed only, never fire on cold load. */
  private hudHadIncompleteOnboardingChallenge: boolean | null = null;

  bindAnimationWidget(widget: AnimationWidgetService): void {
    this.animationWidget = widget;
  }

  /**
   * Deduped entry for claim dialog + HUD complete transition.
   * Safe to call from multiple places — only the first call runs evaluate.
   */
  requestFirstChallengeCompleted(): void {
    if (this.firstChallengeCompletedRequested) {
      return;
    }
    this.firstChallengeCompletedRequested = true;
    void this.run('first_challenge_completed').catch(() => {
      // Allow a later source (HUD / claim) to retry if evaluate never applied.
      this.firstChallengeCompletedRequested = false;
    });
  }

  /**
   * Game-return only: when onboarding challenge progress is already done, call
   * evaluate(`first_challenge_completed`) so CF fills pending_popups and FE can
   * show challenge_completed (without needing reload).
   */
  tryCompleteFirstChallengeAfterGameReturn(
    hud: Data | null | undefined
  ): boolean {
    if (this.firstChallengeCompletedRequested) {
      return true;
    }
    if (
      !this.isOnboardingChallengeProgressMet(hud) &&
      !this.isOnboardingChallengeMarkedComplete(hud)
    ) {
      return false;
    }
    this.requestFirstChallengeCompleted();
    return true;
  }

  /**
   * Watch challenge HUD: when an onboarding challenge flips incomplete → done
   * while the user stays on games page, fire first_challenge_completed once.
   * Cold load / reload must NOT fire here — games_page_load already handles pending.
   */
  onChallengeHudUpdated(hud: Data | null | undefined): void {
    const incomplete = this.hasIncompleteOnboardingChallenge(hud);

    if (this.hudHadIncompleteOnboardingChallenge === null) {
      this.hudHadIncompleteOnboardingChallenge = incomplete;
      return;
    }

    const wasIncomplete = this.hudHadIncompleteOnboardingChallenge;

    if (!wasIncomplete) {
      this.hudHadIncompleteOnboardingChallenge = incomplete;
      return;
    }

    if (incomplete) {
      // Still incomplete — keep watching.
      this.hudHadIncompleteOnboardingChallenge = true;
      return;
    }

    // Incomplete gone — only fire when CF/HUD explicitly marks complete.
    // Avoids false trigger if time_based.challenges briefly empties mid-refresh.
    if (!this.isOnboardingChallengeMarkedComplete(hud)) {
      this.hudHadIncompleteOnboardingChallenge = true;
      return;
    }

    this.hudHadIncompleteOnboardingChallenge = false;
    this.requestFirstChallengeCompleted();
  }

  private hasIncompleteOnboardingChallenge(
    hud: Data | null | undefined
  ): boolean {
    if (!hud) {
      return false;
    }
    const challenges = (hud as Data)?.time_based?.challenges;
    if (!Array.isArray(challenges)) {
      return false;
    }
    return challenges.some(
      (c: { challenge_type?: string; completed?: boolean }) =>
        String(c?.challenge_type || '').toLowerCase() === 'onboarding' &&
        c?.completed !== true
    );
  }

  private isOnboardingChallengeMarkedComplete(
    hud: Data | null | undefined
  ): boolean {
    if (!hud) {
      return false;
    }
    const onboarding = (hud as Data)?.onboarding;
    if (
      onboarding?.completed === true ||
      (onboarding as { completed?: unknown } | undefined)?.completed === 'true'
    ) {
      return true;
    }
    const challenges = (hud as Data)?.time_based?.challenges;
    if (!Array.isArray(challenges)) {
      return false;
    }
    return challenges.some(
      (c: { challenge_type?: string; completed?: boolean }) =>
        String(c?.challenge_type || '').toLowerCase() === 'onboarding' &&
        c?.completed === true
    );
  }

  /** Stamps / achieved >= target (game may update these before `completed: true`). */
  private isOnboardingChallengeProgressMet(
    hud: Data | null | undefined
  ): boolean {
    if (!hud) {
      return false;
    }
    const challenges = (hud as Data)?.time_based?.challenges;
    if (!Array.isArray(challenges)) {
      return false;
    }
    return challenges.some(
      (c: {
        challenge_type?: string;
        completed?: boolean;
        target?: {
          progress?: {
            achieved?: number;
            target?: number;
            stamp_game_images?: unknown[];
          };
        };
      }) => {
        if (String(c?.challenge_type || '').toLowerCase() !== 'onboarding') {
          return false;
        }
        if (c?.completed === true) {
          return true;
        }
        const progress = c?.target?.progress;
        if (!progress) {
          return false;
        }
        const stamps = progress.stamp_game_images;
        const achieved =
          Array.isArray(stamps) && stamps.length > 0
            ? stamps.length
            : Number(progress.achieved ?? 0);
        const target = Number(progress.target ?? 0);
        return (
          Number.isFinite(target) &&
          target > 0 &&
          Number.isFinite(achieved) &&
          achieved >= target
        );
      }
    );
  }

  evaluate(event = 'games_page_load'): Observable<ActiveOnboardingResult | null> {
    return this.authService
      .getV2CloudFunction('evaluategamesonboarding', { event })
      .pipe(
        catchError(() => of(null)),
        take(1)
      )
      .pipe(
        // Callable may return { result: {...} } or the payload directly.
        switchMap((raw) => of(this.unwrapPayload(raw)))
      ) as Observable<ActiveOnboardingResult | null>;
  }

  /** Advance current cue; CF returns the next current_cue. */
  actionCue(cueIndex?: number): Observable<ActiveOnboardingResult | null> {
    const body: Data =
      cueIndex != null && !Number.isNaN(Number(cueIndex))
        ? { cue_index: Number(cueIndex) }
        : {};

    return this.authService
      .getV2CloudFunction('actiongamesonboardingcue', body)
      .pipe(
      catchError((err) => {
        this.authService.errorHandlerLogs(
          '[GamesOnboardingService] actionCue failed',
          err
        );
        return of(null);
      }),
      take(1),
      switchMap((raw) => of(this.unwrapPayload(raw))),
      tap((res) => {
        if (res) {
          this.lastTriggerEvent = 'cue_actioned';
          void this.applyEvaluateResult(res);
        }
      })
    ) as Observable<ActiveOnboardingResult | null>;
  }

  reportEvent(
    event: string,
    extra: { cue_index?: number; popup_id?: string } = {}
  ): Observable<ActiveOnboardingResult | null> {
    // Do NOT auto-apply here — applyEvaluateResult also calls reportEvent and would recurse.
    return this.authService
      .getV2CloudFunction('reportgamesonboardingevent', {
        event,
        ...extra,
      })
      .pipe(
        catchError((err) => {
          this.authService.errorHandlerLogs(
            '[GamesOnboardingService] reportEvent failed',
            err
          );
          return of(null);
        }),
        take(1),
        switchMap((raw) => of(this.unwrapPayload(raw))),
        tap((res) => {
          // Only gate celebrations on events that actually reached the server.
          if (res) {
            this.lastTriggerEvent = event;
          }
        })
      ) as Observable<ActiveOnboardingResult | null>;
  }

  /** Call after a successful prize-box claim — advances award/celebration cues. */
  onFirstPrizeBoxOpened(): Observable<ActiveOnboardingResult | null> {
    return this.reportEvent('first_prize_box_opened').pipe(
      tap((res) => {
        if (res) {
          this.lastTriggerEvent = 'first_prize_box_opened';
          void this.applyEvaluateResult(res);
        }
      })
    );
  }

  private unwrapPayload(raw: unknown): ActiveOnboardingResult | null {
    if (!raw || typeof raw !== 'object') {
      return null;
    }
    const obj = raw as Data;
    const nested = obj['result'] ?? obj['data'];
    if (nested && typeof nested === 'object' && (nested as Data)['status'] != null) {
      return nested as ActiveOnboardingResult;
    }
    if (obj['status'] != null || obj['current_cue'] != null || obj['step_id'] != null) {
      return obj as ActiveOnboardingResult;
    }
    return obj as ActiveOnboardingResult;
  }

  /**
   * Debounced games-page evaluation. Waits for open MatDialogs to close first.
   */
  evaluateOnGamesPageLoad(): Observable<void> {
    return timer(300).pipe(
      switchMap(() => {
        if (this.matDialog.openDialogs.length > 0) {
          return this.matDialog.afterAllClosed.pipe(take(1));
        }
        return of(void 0);
      }),
      switchMap(() => from(this.run('games_page_load'))),
      catchError(() => of(void 0))
    );
  }

  async run(event = 'games_page_load'): Promise<void> {
    // Never drop completion events while another evaluate is in flight —
    // that caused celebration/popup/hands to only appear after leaving and
    // returning to the games page.
    if (this.runInFlight) {
      const queuedPri = this.runEventPriority(this.queuedRunEvent);
      const nextPri = this.runEventPriority(event);
      if (this.queuedRunEvent == null || nextPri >= queuedPri) {
        this.queuedRunEvent = event;
      }
      return;
    }

    this.runInFlight = true;
    try {
      let current: string | null = event;
      while (current) {
        this.queuedRunEvent = null;
        this.syncDismissTrackingToCurrentUser();
        const res = await firstValueFrom(this.evaluate(current));
        // Apply idle too — CF can return pending_popups with no active step.
        // Only stamp lastTriggerEvent after a successful evaluate so a failed
        // request cannot gate celebrations as if the event reached the server.
        if (res && res.status !== 'fail') {
          this.lastTriggerEvent = current;
          await this.applyEvaluateResult(res);
        }
        current = this.queuedRunEvent;
      }
    } finally {
      this.runInFlight = false;
    }
  }

  private runEventPriority(event: string | null | undefined): number {
    if (!event) {
      return 0;
    }
    if (
      event === 'first_challenge_completed' ||
      event === 'first_prize_box_opened' ||
      event === 'first_prize_unlocked'
    ) {
      return 100;
    }
    if (event === 'games_page_load') {
      return 10;
    }
    return 50;
  }

  /** Narrator tip closed — advance onboarding cue. */
  onNarratorTipDismissed(): void {
    if (this.pendingNarratorCueIndex == null) {
      return;
    }
    const index = this.pendingNarratorCueIndex;
    this.pendingNarratorCueIndex = null;
    this.actionCue(index).subscribe();
  }

  /**
   * Micro-anim tapped / cleared — AnimationWidgetService already set status false;
   * advance onboarding cue when this location was the pending cue.
   */
  onMicroAnimationActioned(locationId: string): void {
    if (!locationId) {
      return;
    }

    const normalizedTap = this.normalizeMicroAnimLocation(locationId);
    const pendingConfigured = this.pendingMicroAnimLocationId
      ? this.normalizeMicroAnimLocation(this.pendingMicroAnimLocationId)
      : null;

    // Prize-box journey: HUD shop_entry is a gateway before in-shop hands
    // (info icon / buy button / info close). Keep the shop open and reveal
    // the real hand — do not advance the cue yet.
    if (
      normalizedTap === 'prize_box_shop_entry' &&
      pendingConfigured &&
      this.isInShopPrizeBoxLocation(pendingConfigured)
    ) {
      this.pendingMicroAnimDisplayLocationId = pendingConfigured;
      this.enableMicroAnimationFromFe(pendingConfigured, 5);
      return;
    }

    const matchesPending =
      normalizedTap === pendingConfigured ||
      locationId === this.pendingMicroAnimLocationId ||
      locationId === this.pendingMicroAnimDisplayLocationId ||
      normalizedTap === this.pendingMicroAnimDisplayLocationId;
    if (!matchesPending) {
      return;
    }
    const index = this.pendingMicroAnimCueIndex;
    this.pendingMicroAnimCueIndex = null;
    this.pendingMicroAnimLocationId = null;
    this.pendingMicroAnimDisplayLocationId = null;
    this.actionCue(index ?? undefined).subscribe();
  }

  /** Map CF/seed aliases onto FE widget location ids. */
  private normalizeMicroAnimLocation(locationId: string): string {
    const id = String(locationId || '').trim();
    if (
      id === 'prize_box_info_icon' ||
      id === 'prize_box_info' ||
      id === 'prize_box_info_tap'
    ) {
      return 'prize_box_info_button';
    }
    if (id === 'prize_box_info_close_button' || id === 'prize_box_info_close_icon') {
      return 'prize_box_info_close';
    }
    return id;
  }

  private isInShopPrizeBoxLocation(locationId: string): boolean {
    const id = this.normalizeMicroAnimLocation(locationId);
    return (
      id === 'prize_box_buy_button' ||
      id === 'prize_box_info_button' ||
      id === 'prize_box_info_close'
    );
  }

  private async applyEvaluateResult(res: ActiveOnboardingResult): Promise<void> {
    if (this.applyInFlight) {
      // Keep the newest payload + the trigger that produced it so celebration
      // gating cannot apply the wrong lastTriggerEvent when dequeued.
      this.queuedApply = {
        res,
        triggerEvent: this.lastTriggerEvent,
      };
      return;
    }
    this.applyInFlight = true;
    try {
      let current: ActiveOnboardingResult | null = res;
      let triggerForCurrent = this.lastTriggerEvent;
      while (current) {
        this.queuedApply = null;
        this.lastTriggerEvent = triggerForCurrent;
        await this.applyEvaluateResultInner(current);
        const queued = this.queuedApply;
        this.queuedApply = null;
        current = queued?.res ?? null;
        triggerForCurrent = queued?.triggerEvent ?? this.lastTriggerEvent;
      }
    } finally {
      this.applyInFlight = false;
    }
  }

  private async applyEvaluateResultInner(res: ActiveOnboardingResult): Promise<void> {
    this.activeStepId.set(res.step_id ?? res.step?.step_id ?? null);
    this.lastResult.set(res);
    this.qaLog.set(
      [
        res.step_id ?? 'idle',
        `cue=${res.step_state?.current_cue ?? res.current_cue?.index ?? '—'}`,
        res.current_cue?.type ?? '—',
        res.pending_popups?.[0] ? `popup=${res.pending_popups[0]}` : '',
      ]
        .filter(Boolean)
        .join(' · ')
    );

    // Tip → drawer hand must run before challenge_completed FX/popup. CF #1153
    // can already have pending_popups parked while current_cue is still the hand.
    const cueForGate =
      res.current_cue ??
      (res.client_cues ?? []).find((c) => !!c?.type) ??
      null;
    const cueForGateType = String(cueForGate?.type || '').toLowerCase();
    const cueForGateLoc = String(cueForGate?.location_id || '');
    const drawerHandBeforeChallengeFx =
      (cueForGateType === 'micro_animation' &&
        (cueForGateLoc === 'challenge_drawer_open' ||
          cueForGateLoc.includes('challenge_drawer'))) ||
      (!!this.pendingMicroAnimLocationId &&
        (this.pendingMicroAnimLocationId === 'challenge_drawer_open' ||
          this.pendingMicroAnimLocationId.includes('challenge_drawer')));

    // Challenge complete FX: CF completes the step on first_challenge_completed and
    // moves to prize-box — celebration/popup cues are never presented. Play once here.
    if (!drawerHandBeforeChallengeFx) {
      await this.maybePlayChallengeCompleteFx(res);
    }
    // Prize-box open FX for new + existing cohorts (celebration + pending popup).
    await this.maybePlayPrizeBoxOpenedFx(res);

    // FX / popup_dismissed report may have advanced the step (prize-box hand).
    // Continue from the latest payload, not the stale pre-FX evaluate result.
    let latest = this.lastResult() ?? res;

    // Claim→CF lag: completion event fired but step not advanced yet — one refresh
    // so prize-box micro_animation hand appears immediately (not on next page visit).
    if (
      this.lastTriggerEvent === 'first_challenge_completed' &&
      latest.step_id !== 'new_first_prize_box' &&
      !this.pendingMicroAnimLocationId
    ) {
      try {
        const retry = await firstValueFrom(this.evaluate('games_page_load'));
        if (retry && retry.status !== 'fail') {
          this.lastResult.set(retry);
          latest = retry;
          this.activeStepId.set(
            retry.step_id ?? retry.step?.step_id ?? null
          );
        }
      } catch {
        /* ignore */
      }
    }
    res = latest;

    // Engine pending_popups first — must not be starved by micro-anim / other cues.
    this.syncDismissTrackingToCurrentUser();
    const pendingRaw = res.pending_popups?.[0];
    const pendingId = pendingRaw
      ? normalizeGameOnboardingPopupId(pendingRaw) ?? pendingRaw
      : null;
    if (pendingId && this.hasDismissedPendingPopup(pendingId)) {
      this.qaLog.set(
        `${this.qaLog()} · skip pending ${pendingId} (already dismissed this uid)`
      );
      // CF often keeps echoing pending_popups after FE dismiss — re-report so
      // server can clear, without re-opening the dialog.
      try {
        const cleared = await firstValueFrom(
          this.reportEvent('popup_dismissed', { popup_id: pendingId })
        );
        if (cleared) {
          this.lastResult.set(cleared);
        }
      } catch {
        /* ignore */
      }
    } else if (pendingId) {
      const pendingIsChallenge =
        pendingId === 'challenge_completed' || pendingId === 'challenge_intro';
      const nextCue =
        res.current_cue ??
        (res.client_cues ?? []).find((c) => !!c?.type) ??
        null;
      const nextCueType = String(nextCue?.type || '').toLowerCase();
      const nextCueLocation = String(nextCue?.location_id || '');
      // CF #1153 parks challenge_completed in pending_popups. That must NOT
      // open (or early-return) before tip → challenge_drawer_open hand enables.
      // Match by active micro cue / pending hand — not only lastTriggerEvent —
      // so games_page_load / cue_actioned / shownBefore-auto-actionCue all work.
      const drawerHandCueActive =
        nextCueType === 'micro_animation' &&
        (nextCueLocation === 'challenge_drawer_open' ||
          nextCueLocation.includes('challenge_drawer'));
      const drawerHandPending =
        !!this.pendingMicroAnimLocationId &&
        (this.pendingMicroAnimLocationId === 'challenge_drawer_open' ||
          this.pendingMicroAnimLocationId.includes('challenge_drawer'));
      const deferPendingForDrawerHand =
        drawerHandCueActive || drawerHandPending;
      // Skip late challenge_completed echoes after the prize-box step starts.
      if (
        pendingIsChallenge &&
        !this.shouldShowChallengeCompletedPopup(res, true) &&
        !deferPendingForDrawerHand
      ) {
        this.qaLog.set(
          `${this.qaLog()} · skip late pending ${pendingId} (after challenge step)`
        );
        try {
          const cleared = await firstValueFrom(
            this.reportEvent('popup_dismissed', { popup_id: pendingId })
          );
          if (cleared) {
            this.lastResult.set(cleared);
          }
        } catch {
          /* ignore */
        }
      } else if (deferPendingForDrawerHand) {
        this.qaLog.set(
          `${this.qaLog()} · defer pending ${pendingId} (drawer hand first)`
        );
        // Fall through to presentCue / enableStatusFB — do not open or clear pending.
      } else {
        this.qaLog.set(`${this.qaLog()} · opening pending ${pendingId}`);
        // Ensure handlePopupCue does not no-op on a stale reportKey after idle echoes.
        this.lastReportedPopupKey = null;
        // Journey celebrations often live on a cue CF already skipped when it
        // parked the popup in pending_popups — play confetti with the popup.
        this.playCelebrationForPopup(pendingId);
        // Snapshot unlocker/prize-box hand before popup — CF dismiss reply may
        // drop client_cues while still echoing pending_popups.
        const cueBeforePopup = nextCue;
        await this.handlePopupCue(pendingId, 'popup_dismissed', res, null, true);
        const after = this.lastResult() ?? res;
        const cueAfter =
          after.current_cue ??
          (after.client_cues ?? []).find((c) => !!c?.type) ??
          null;
        // Always fall through so micro_animation (drawer / unlocker hand) still presents.
        // Do not return early when CF re-echoes the same pending_popups.
        if (
          !cueAfter &&
          cueBeforePopup &&
          String(cueBeforePopup.type || '').toLowerCase() === 'micro_animation'
        ) {
          this.lastResult.set({
            ...after,
            current_cue: cueBeforePopup,
            client_cues: after.client_cues?.length
              ? after.client_cues
              : [cueBeforePopup],
          });
        }
      }
    }

    // Pending / FX paths may have refreshed lastResult — never continue on stale res.
    res = this.lastResult() ?? res;

    // Prefer explicit current_cue; fall back to client_cues.
    const cue =
      res.current_cue ??
      (res.client_cues ?? []).find((c) => !!c?.type) ??
      null;

    // Only the active micro_animation cue may stay enabled (HATDEV-90338 Part F).
    const cueType = String(cue?.type || '').toLowerCase();
    let keepLocationId =
      cueType === 'micro_animation' && cue?.location_id
        ? this.normalizeMicroAnimLocation(String(cue.location_id))
        : null;
    // Remap in-shop hands → HUD shop entry while gift-box shop is closed.
    if (
      keepLocationId &&
      this.isInShopPrizeBoxLocation(keepLocationId) &&
      !this.isGiftBoxShopOpen()
    ) {
      keepLocationId = 'prize_box_shop_entry';
    }
    this.animationWidget?.enforceExclusiveStatus(keepLocationId);

    if (cue) {
      this.presentCue(cue, res);
    }

    const popup = (res.client_cues ?? []).find(
      (c) => c?.type === 'popup' && c?.popup_id
    );
    if (!popup?.popup_id) {
      return;
    }

    const popupId =
      normalizeGameOnboardingPopupId(popup.popup_id) ?? popup.popup_id;

    const foundIndex =
      res.step?.cues?.findIndex(
        (c) => c?.type === 'popup' && c?.popup_id === popup.popup_id
      ) ?? -1;
    const cueIndex =
      foundIndex >= 0 ? foundIndex : res.step_state?.current_cue;

    // Normal client_cues popups must not use engine-pending force/mock flags.
    await this.handlePopupCue(
      popupId,
      res.step?.completion_event ?? 'popup_dismissed',
      res,
      cueIndex,
      false
    );
  }

  /**
   * Show challenge_completed while CF still parks it in pending_popups.
   * CF often advances step (prize_box / unlock) before FE opens the popup —
   * do NOT require still being on the challenge step. Only block after the
   * user has started prize-box open / unlock (avoids late echo after those).
   */
  private shouldShowChallengeCompletedPopup(
    _res: ActiveOnboardingResult,
    pendingIsChallenge: boolean
  ): boolean {
    // Terminal states first — block late challenge_completed echoes after
    // prize-box open / unlock even if lastTriggerEvent is still first_challenge_completed.
    if (
      this.lastTriggerEvent === 'first_prize_box_opened' ||
      this.lastTriggerEvent === 'prize_box_gained_dismissed' ||
      this.lastTriggerEvent === 'first_prize_unlocked' ||
      this.prizeBoxOpenedFxDone
    ) {
      return false;
    }
    if (this.lastTriggerEvent === 'first_challenge_completed') {
      return true;
    }
    if (!pendingIsChallenge) {
      return false;
    }
    return true;
  }

  /** One-shot party popper + journey popup when first challenge is done. */
  private async maybePlayChallengeCompleteFx(
    res: ActiveOnboardingResult
  ): Promise<void> {
    if (this.challengeCompleteFxDone) {
      return;
    }
    let stored = false;
    try {
      stored = sessionStorage.getItem('games_ob_fx_first_challenge') === '1';
    } catch {
      /* ignore */
    }
    if (stored) {
      this.challengeCompleteFxDone = true;
      return;
    }

    const pendingRaw = res.pending_popups?.[0];
    const pendingId = pendingRaw
      ? normalizeGameOnboardingPopupId(pendingRaw) ?? pendingRaw
      : null;
    const pendingIsChallenge =
      pendingId === 'challenge_completed' || pendingId === 'challenge_intro';
    // Demo fix (Filip): challenge popup must fire on challenge complete — not
    // after prize-box open when CF still echoes pending challenge_completed.
    if (!this.shouldShowChallengeCompletedPopup(res, pendingIsChallenge)) {
      return;
    }

    this.challengeCompleteFxDone = true;
    try {
      sessionStorage.setItem('games_ob_fx_first_challenge', '1');
    } catch {
      /* ignore */
    }

    this.playCelebration('challenge_completed_celebration');
    // Pending path opens the same popup next — skip duplicate open here.
    if (pendingIsChallenge) {
      return;
    }

    const opened = await this.handlePopupCue(
      'challenge_completed',
      'popup_dismissed',
      res,
      null,
      true
    );
    if (!opened) {
      // Allow retry this session if the popup was blocked / inactive.
      this.challengeCompleteFxDone = false;
      try {
        sessionStorage.removeItem('games_ob_fx_first_challenge');
      } catch {
        /* ignore */
      }
    }
  }

  /**
   * One-shot party popper + prize_box_opened journey popup for new/existing
   * cohorts when the domain event fires or pending_popups parks the popup.
   */
  private async maybePlayPrizeBoxOpenedFx(
    res: ActiveOnboardingResult
  ): Promise<void> {
    if (this.prizeBoxOpenedFxDone) {
      return;
    }
    let stored = false;
    try {
      stored = sessionStorage.getItem('games_ob_fx_prize_box_opened') === '1';
    } catch {
      /* ignore */
    }
    if (stored) {
      this.prizeBoxOpenedFxDone = true;
      return;
    }

    const triggerMatch =
      this.lastTriggerEvent === 'first_prize_box_opened' ||
      this.lastTriggerEvent === 'prize_box_gained_dismissed';
    const pendingRaw = res.pending_popups?.[0];
    const pendingId = pendingRaw
      ? normalizeGameOnboardingPopupId(pendingRaw) ?? pendingRaw
      : null;
    const pendingIsPrizeBox =
      pendingId === 'prize_box_opened' || pendingId === 'prize_box_gained';
    const onPrizeUnlockStep =
      res.step_id === 'new_first_prize_box' ||
      res.step_id === 'existing_prize_box';

    // Only fire on the domain event, or when CF already parked the popup
    // while status is idle / step moved past the celebration cue.
    if (!triggerMatch && !pendingIsPrizeBox) {
      return;
    }
    if (!triggerMatch && !onPrizeUnlockStep && res.status !== 'idle') {
      return;
    }

    this.prizeBoxOpenedFxDone = true;
    try {
      sessionStorage.setItem('games_ob_fx_prize_box_opened', '1');
    } catch {
      /* ignore */
    }

    // If pending path will open the same popup next, skip duplicate open here.
    if (pendingIsPrizeBox) {
      return;
    }

    this.playCelebrationForPopup('prize_box_opened');
    const opened = await this.handlePopupCue(
      'prize_box_opened',
      'popup_dismissed',
      res,
      null,
      true
    );
    if (!opened) {
      this.prizeBoxOpenedFxDone = false;
      try {
        sessionStorage.removeItem('games_ob_fx_prize_box_opened');
      } catch {
        /* ignore */
      }
    }
  }

  /** Confetti when opening a journey popup (pending or completion FX). */
  private playCelebrationForPopup(popupId: string): void {
    const normalized =
      normalizeGameOnboardingPopupId(popupId) ?? String(popupId || '');
    if (
      normalized === 'challenge_completed' ||
      normalized === 'prize_box_opened' ||
      normalized === 'prize_unlocked'
    ) {
      this.playCelebration(`${normalized}_celebration`);
    }
  }

  private isGiftBoxShopOpen(): boolean {
    return !!document.querySelector('.gift-box-bottom-sheet, .cdk-overlay-pane.gift-box-bottom-sheet');
  }

  /**
   * Show popup once, report dismiss once. Skips if already reported for this key
   * (prevents reportgamesonboardingevent storms when CF still returns pending_popups).
   * @returns true when the user saw and closed the popup (cta/dismiss).
   */
  private async handlePopupCue(
    popupId: string,
    event: string,
    res: ActiveOnboardingResult,
    cueIndex?: number | null,
    fromEnginePending = false
  ): Promise<boolean> {
    const normalized =
      normalizeGameOnboardingPopupId(popupId) ?? popupId;
    const reportKey = [
      normalized,
      event,
      res.step_id ?? '',
      res.step_state?.current_cue ?? '',
    ].join('|');
    // Engine pending must open unless this uid already dismissed it.
    if (
      this.lastReportedPopupKey === reportKey &&
      (!fromEnginePending || this.hasDismissedPendingPopup(normalized))
    ) {
      return false;
    }

    if (fromEnginePending && this.hasDismissedPendingPopup(normalized)) {
      return false;
    }

    // Wait for any open dialog (narrator / product) so journey popup is not dropped.
    if (this.matDialog.openDialogs.length > 0) {
      await firstValueFrom(this.matDialog.afterAllClosed.pipe(take(1)));
    }

    // Engine pending_popups must open even if Firestore doc is missing/inactive
    // or another dialog previously blocked a non-force open (QA works via force).
    const close = await firstValueFrom(
      this.popupService.open(normalized, {
        force: fromEnginePending,
        allowWithOthers: fromEnginePending,
        useMockFallback: fromEnginePending,
      })
    );

    if (close.reason === 'unknown' || close.reason === 'inactive') {
      this.qaLog.set(
        `${this.qaLog()} · popup ${normalized} blocked (${close.reason})`
      );
      // Do NOT mark reported — allow next evaluate to retry.
      return false;
    }

    this.lastReportedPopupKey = reportKey;
    const next = await firstValueFrom(
      this.reportEvent(event, {
        popup_id: normalized,
        ...(cueIndex != null &&
        !Number.isNaN(Number(cueIndex)) &&
        Number(cueIndex) >= 0
          ? { cue_index: Number(cueIndex) }
          : {}),
      })
    );
    if (!next) {
      // User saw the popup but report failed — do not locally mask pending
      // echoes so a later evaluate can recover.
      return true;
    }

    // Only engine pending_popups should suppress future echoes via this set.
    // Mark after a successful report so a failed report can still recover.
    if (fromEnginePending) {
      this.markPendingPopupDismissed(normalized);
    }

    this.lastResult.set(next);
    // Only continue if CF moved past this same pending popup.
    const nextPendingRaw = next.pending_popups?.[0];
    const nextPending = nextPendingRaw
      ? normalizeGameOnboardingPopupId(nextPendingRaw) ?? nextPendingRaw
      : undefined;
    const nextClientPopup = (next.client_cues ?? []).find(
      (c) => c?.type === 'popup' && c?.popup_id
    );
    const nextClientId = nextClientPopup?.popup_id
      ? normalizeGameOnboardingPopupId(nextClientPopup.popup_id) ??
        nextClientPopup.popup_id
      : null;
    // Same pending still returned after dismiss — stop (local dismiss already marked).
    if (
      nextPending === normalized ||
      nextClientId === normalized ||
      (nextPending != null && this.hasDismissedPendingPopup(nextPending))
    ) {
      return true;
    }
    await this.applyEvaluateResultInner(next);
    return true;
  }

  private markPendingPopupDismissed(popupId: string): void {
    this.syncDismissTrackingToCurrentUser();
    this.dismissedPendingPopups.add(popupId);
    // Memory-only for skip gating. Persisting to sessionStorage caused
    // idle+pending_popups to never reopen after refresh while CF still
    // echoed the id (UT FAIL: journey popup never opens).
  }

  private hasDismissedPendingPopup(popupId: string): boolean {
    this.syncDismissTrackingToCurrentUser();
    return this.dismissedPendingPopups.has(popupId);
  }

  /** Avoid leaking dismiss flags across MSNs on the same browser/tab. */
  private syncDismissTrackingToCurrentUser(): void {
    const uid = this.authService.userId || 'anon';
    if (this.dismissTrackingUid === uid) {
      return;
    }
    this.dismissTrackingUid = uid;
    this.dismissedPendingPopups.clear();
    this.lastReportedPopupKey = null;
    // Tip suppression keys are uid-prefixed; clear the in-memory set on switch.
    this.presentedNarratorKeys.clear();
  }

  private dismissedPopupStorageKey(popupId: string): string {
    const uid = this.dismissTrackingUid || 'anon';
    return `games_ob_dismissed_popup_${uid}_${popupId}`;
  }

  private presentCue(cue: OnboardingCue, res: ActiveOnboardingResult): void {
    const type = String(cue?.type || '').toLowerCase();
    const cueIndex =
      cue.index != null
        ? Number(cue.index)
        : Number(res.step_state?.current_cue ?? 0);

    const cueKey = [
      type,
      cueIndex,
      cue?.template_id ?? '',
      cue?.location_id ?? '',
      cue?.popup_id ?? '',
    ].join('|');
    if (cueKey === this.lastPresentedCueKey) {
      return;
    }

    // Event-driven celebrations: check the trigger BEFORE stamping
    // lastPresentedCueKey, otherwise a wrong-trigger evaluate permanently
    // suppresses the celebration when the correct event later arrives.
    if (type === 'celebration') {
      const advanceOn = String(cue['advance_on'] ?? '');
      if (advanceOn.startsWith('event:')) {
        const neededEvent = advanceOn.slice('event:'.length);
        if (this.lastTriggerEvent !== neededEvent) {
          return;
        }
      }
    }

    this.lastPresentedCueKey = cueKey;

    if (type === 'narrator_tip' && cue.template_id) {
      const stepId = res.step_id ?? res.step?.step_id ?? 'step';
      const tipKey = `${stepId}|${cueIndex}|${cue.template_id}`;
      const uid = this.authService.userId || 'anon';
      // Memory + session keys are uid-scoped so MSN switches do not suppress tips.
      const tipMemoryKey = `${uid}|${tipKey}`;
      const storageKey = `games_ob_tip_${uid}_${tipKey}`;
      let shownBefore = this.presentedNarratorKeys.has(tipMemoryKey);
      try {
        shownBefore =
          shownBefore || sessionStorage.getItem(storageKey) === '1';
      } catch {
        /* private mode — memory set is enough */
      }

      // Already shown this tip for this step cue (reload / re-evaluate) — advance
      // to micro_animation instead of opening the dialog 2–3 times.
      if (shownBefore) {
        this.pendingNarratorCueIndex = null;
        this.lastPresentedCueKey = null;
        timer(50)
          .pipe(take(1))
          .subscribe(() => this.actionCue(cueIndex).subscribe());
        return;
      }

      // Mark shown immediately so a second games_page_load cannot open the same
      // tip before the 400ms dialog confirm (ngOnInit + ionViewWillEnter race).
      this.pendingNarratorCueIndex = cueIndex;
      this.markNarratorTipShown(tipMemoryKey, storageKey);
      void this.presentNarratorTip(
        String(cue.template_id),
        cueIndex,
        tipMemoryKey,
        storageKey
      );
      return;
    }

    // Inbox is issued by CF (cues_fired). FE must action the cue so micro_animation can start.
    if (type === 'inbox_message') {
      timer(300)
        .pipe(take(1))
        .subscribe(() => this.actionCue(cueIndex).subscribe());
      return;
    }

    if (type === 'micro_animation' && cue.location_id) {
      const configuredLocation = this.normalizeMicroAnimLocation(
        String(cue.location_id)
      );
      this.pendingMicroAnimLocationId = configuredLocation;
      this.pendingMicroAnimCueIndex = cueIndex;

      // In-shop hands cannot render while the gift-box sheet is closed —
      // park on HUD shop_entry until the user opens the shop (same for
      // buy_button, info_button / info_icon, info_close).
      let displayLocation = configuredLocation;
      if (
        this.isInShopPrizeBoxLocation(configuredLocation) &&
        !this.isGiftBoxShopOpen()
      ) {
        displayLocation = 'prize_box_shop_entry';
      }
      this.pendingMicroAnimDisplayLocationId = displayLocation;
      this.enableMicroAnimationFromFe(
        displayLocation,
        Number(cue.duration ?? 5)
      );
      return;
    }

    if (type === 'award') {
      // Server issues on domain event; nothing to present client-side.
      return;
    }

    if (type === 'celebration') {
      const advanceOn = String(cue['advance_on'] ?? '');
      // Event-driven celebrations must NOT fire when the hand cue merely advances
      // onto this cue index — only when the domain event actually arrives.
      if (advanceOn.startsWith('event:')) {
        const neededEvent = advanceOn.slice('event:'.length);
        if (this.lastTriggerEvent !== neededEvent) {
          return;
        }
      }
      this.playCelebration(cue.animation_id);
      if (!advanceOn.startsWith('event:')) {
        timer(2800)
          .pipe(take(1))
          .subscribe(() => this.actionCue(cueIndex).subscribe());
      }
      return;
    }

    if (type === 'popup') {
      // Handled via pending_popups / client_cues popup path in applyEvaluateResult.
      return;
    }
  }

  private markNarratorTipShown(tipMemoryKey: string, storageKey: string): void {
    this.presentedNarratorKeys.add(tipMemoryKey);
    try {
      sessionStorage.setItem(storageKey, '1');
    } catch {
      /* ignore */
    }
  }

  private async presentNarratorTip(
    templateId: string,
    cueIndex: number,
    tipMemoryKey: string,
    storageKey: string
  ): Promise<void> {
    const openBefore = this.matDialog.openDialogs.length;

    // Tip already open (CF inbox + FE race) — wire dismiss so this cue advances.
    const existingTip = this.matDialog.openDialogs.find(
      (d) => d.componentInstance instanceof NarratorTipsComponent
    );
    if (existingTip) {
      this.markNarratorTipShown(tipMemoryKey, storageKey);
      existingTip.afterClosed().pipe(take(1)).subscribe(() => {
        this.onNarratorTipDismissed();
      });
      return;
    }

    try {
      // CF already issued the tip via issue_message_bundle. Skip inbox + closeAll
      // so the same template does not reopen from inbox / stacked dialogs.
      await this.narratorTipService.narratorTipEvent(templateId, {
        skipInbox: true,
        skipCloseAll: true,
      });
    } catch (err) {
      this.authService.errorHandlerLogs(
        '[GamesOnboardingService] narrator tip failed',
        err
      );
      this.clearNarratorTipShown(tipMemoryKey, storageKey);
      this.pendingNarratorCueIndex = null;
      this.lastPresentedCueKey = null;
      return;
    }

    timer(400)
      .pipe(take(1))
      .subscribe(() => {
        const tipRef = this.matDialog.openDialogs.find(
          (d) => d.componentInstance instanceof NarratorTipsComponent
        );
        if (tipRef) {
          tipRef.afterClosed().pipe(take(1)).subscribe(() => {
            this.onNarratorTipDismissed();
          });
          return;
        }

        // Tip could not open — clear shown mark; still advance so hands are not stalled.
        this.clearNarratorTipShown(tipMemoryKey, storageKey);
        this.pendingNarratorCueIndex = cueIndex;
        this.lastPresentedCueKey = null;
        if (this.matDialog.openDialogs.length <= openBefore) {
          this.onNarratorTipDismissed();
        } else {
          this.pendingNarratorCueIndex = null;
        }
      });
  }

  private clearNarratorTipShown(tipMemoryKey: string, storageKey: string): void {
    this.presentedNarratorKeys.delete(tipMemoryKey);
    try {
      sessionStorage.removeItem(storageKey);
    } catch {
      /* ignore */
    }
  }

  /**
   * Firebase micro_animations status stays false until FE turns it on for the active cue.
   * enableStatusFB clears every other location first (only one hand at a time).
   */
  enableMicroAnimationFromFe(locationId: string, duration = 5): void {
    if (!locationId || !this.animationWidget) {
      return;
    }

    timer(400)
      .pipe(take(1))
      .subscribe(() => {
        if (!this.animationWidget) {
          return;
        }
        this.animationWidget.enableStatusFB(locationId, duration).subscribe({
          error: (err) =>
            this.authService.errorHandlerLogs(
              '[GamesOnboardingService] enable micro_animation failed',
              err
            ),
        });
      });
  }

  playCelebration(_animationId?: string): void {
    if (this.celebrationClearTimer) {
      clearTimeout(this.celebrationClearTimer);
    }

    this.celebrationActive.set(false);
    queueMicrotask(() => {
      this.celebrationActive.set(true);
      try {
        let canvas = document.getElementById(
          'onboarding-confetti-canvas'
        ) as HTMLCanvasElement | null;
        if (!canvas) {
          canvas = document.createElement('canvas');
          canvas.id = 'onboarding-confetti-canvas';
          canvas.width = window.innerWidth;
          canvas.height = window.innerHeight;
          canvas.style.position = 'fixed';
          canvas.style.inset = '0';
          canvas.style.width = '100%';
          canvas.style.height = '100%';
          canvas.style.pointerEvents = 'none';
          canvas.style.zIndex = '20000';
          document.body.appendChild(canvas);
        }

        const animation = confetti.create(canvas, {
          resize: true,
          useWorker: true,
        });
        animation({
          particleCount: 120,
          spread: 80,
          startVelocity: 40,
          origin: { y: 0.25 },
        });
      } catch (err) {
        this.authService.errorHandlerLogs(
          '[GamesOnboardingService] celebration failed',
          err
        );
      }
    });

    this.celebrationClearTimer = setTimeout(() => {
      this.celebrationActive.set(false);
      this.celebrationClearTimer = undefined;
    }, 3500);
  }

  /** QA: force-enable a hand (exclusive). */
  qaEnableHand(locationId: string, duration = 5): void {
    this.qaLog.set(`enable ${locationId}`);
    this.enableMicroAnimationFromFe(locationId, duration);
  }

  /** QA: clear every micro-animation status. */
  qaClearHands(): void {
    this.animationWidget?.enforceExclusiveStatus(null);
    this.qaLog.set('cleared all hands');
  }

  /** QA: open an onboarding popup by id (force — no CF report). */
  async qaOpenPopup(popupId: string): Promise<void> {
    const normalized = normalizeGameOnboardingPopupId(popupId);
    if (!normalized) {
      this.qaLog.set(`unknown popup ${popupId}`);
      return;
    }
    this.qaLog.set(`open ${normalized}`);
    // Always re-fetch Firestore so QA is not stuck on a prior null/mock cache.
    this.popupService.clearDocCache();
    await firstValueFrom(this.popupService.open(normalized, { force: true }));
  }

  /** QA: clear local/session dismiss masks so pending_popups can show again. */
  qaClearDismissedPopups(): void {
    this.syncDismissTrackingToCurrentUser();
    this.popupService.clearDocCache();
    this.challengeCompleteFxDone = false;
    this.prizeBoxOpenedFxDone = false;
    this.lastPresentedCueKey = null;
    this.presentedNarratorKeys.clear();
    try {
      sessionStorage.removeItem('games_ob_fx_first_challenge');
      sessionStorage.removeItem('games_ob_fx_prize_box_opened');
    } catch {
      /* ignore */
    }
    // Clear uid-scoped tip suppression keys for the current user.
    try {
      const uid = this.authService.userId || 'anon';
      const prefix = `games_ob_tip_${uid}_`;
      const toRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key?.startsWith(prefix)) {
          toRemove.push(key);
        }
      }
      for (const key of toRemove) {
        sessionStorage.removeItem(key);
      }
    } catch {
      /* ignore */
    }
    const ids = [
      'challenge_completed',
      'prize_box_opened',
      'prize_unlocked',
      'challenge_intro',
      'prize_box_gained',
    ];
    for (const id of ids) {
      this.dismissedPendingPopups.delete(id);
      try {
        sessionStorage.removeItem(this.dismissedPopupStorageKey(id));
      } catch {
        /* ignore */
      }
    }
    this.lastReportedPopupKey = null;
    this.firstChallengeCompletedRequested = false;
    this.qaLog.set('cleared popup dismiss flags');
  }
}
